import * as THREE from 'three';
import { Volume, BoxVolume } from './Volume.js';
import { Utils } from '../utils.js';
import { EventDispatcher } from '../EventDispatcher.js';

const customAssetType = 'Object3D';
const libraryMeshType = 'Group';

export class VolumeTool extends EventDispatcher {
    constructor(viewer) {
        super();

        this.viewer = viewer;
        this.renderer = viewer.renderer;

        this.addEventListener('start_inserting_volume', (e) => {
            this.viewer.dispatchEvent({
                type: 'cancel_insertions',
            });
        });

        this.scene = new THREE.Scene();
        this.scene.name = 'scene_volume';

        this.viewer.inputHandler.registerInteractiveScene(this.scene);

        this.onRemove = (e) => {
            this.scene.remove(e.volume);
        };

        this.onAdd = (e) => {
            this.scene.add(e.volume);
        };

        for (let volume of viewer.scene.volumes) {
            this.onAdd({ volume: volume });
        }

        this.viewer.inputHandler.addEventListener('delete', (event) => {
            const { selection } = event;

            selection.forEach((item) => {
                if (item instanceof BoxVolume && item.type === customAssetType) {
                    // delete custom asset
                    if (item.clip || confirm('Delete custom asset?')) {
                        this.viewer.scene.removeVolume(item);
                    }
                } else if (item.type === libraryMeshType) {
                    // delete library mesh
                    if (confirm('Delete library mesh?')) {
                        this.viewer.scene.removeMesh(item);
                    }
                }
            });
        });

        window.addEventListener('keydown', (event) => {
            const deleteKeyCodes = new Set(['Delete', 'Backspace']);
            const isEditing = ['INPUT', 'TEXTAREA'].includes(event.target.tagName) || event.target.isContentEditable;

            if (isEditing || !deleteKeyCodes.has(event.code) || !(this.viewer && this.viewer.inputHandler)) return;

            const { inputHandler } = this.viewer;
            const { selection } = inputHandler;

            const selectedCustomAssets = selection.filter((item) => item instanceof Volume);
            const selectedLibraryMeshes = selection.filter((item) => item.type === libraryMeshType);

            if (selectedCustomAssets.length === 0 && selectedLibraryMeshes.length === 0) return;

            inputHandler.dispatchEvent({
                type: 'delete',
                selection: [...selectedCustomAssets, ...selectedLibraryMeshes],
            });
        });

        viewer.addEventListener('update', this.update.bind(this));
        viewer.addEventListener('render.pass.scene', (e) => this.render(e));
        viewer.addEventListener('scene_changed', this.onSceneChange.bind(this));

        viewer.scene.addEventListener('volume_added', this.onAdd);
        viewer.scene.addEventListener('volume_removed', this.onRemove);
    }

    onSceneChange(e) {
        if (e.oldScene) {
            e.oldScene.removeEventListeners('volume_added', this.onAdd);
            e.oldScene.removeEventListeners('volume_removed', this.onRemove);
        }

        e.scene.addEventListener('volume_added', this.onAdd);
        e.scene.addEventListener('volume_removed', this.onRemove);
    }

    startInsertion(args = {}, customCallback) {
        let volume;
        if (args.type) {
            volume = new args.type();
        } else {
            volume = new BoxVolume(args);
        }

        volume.clip = args.clip || false;
        volume.name = args.name || 'Volume';
        volume.clipType = args.clipType || 'Potree';
        volume.locked = false;
        volume.newlyCreated = !volume.clip;

        this.dispatchEvent({
            type: 'start_inserting_volume',
            volume: volume,
        });

        this.viewer.scene.addVolume(volume);
        this.scene.add(volume);

        let cancel = {
            callback: null,
        };

        let drag = async (e) => {
            let camera = this.viewer.scene.getActiveCamera();
            let I;
            if (!args.clip) {
                I = Utils.getMousePointCloudIntersection(e.drag.end, camera, this.viewer, this.viewer.scene.pointclouds, {
                    pickClipped: true,
                    pickInvisible: false,
                });
                if (!I && args.ifcHelper) {
                    I = Utils.getMouseIfcIntersection(e.drag.end, camera, this.renderer, args.ifcHelper);
                    // Light (Fragment) models are managed by OBC and not visible to AABB — use castRayIfc as fallback
                    if (!I && args.ifcHelper.obcRaycaster) {
                        const ifcPoint = await args.ifcHelper.castRayIfc();
                        if (ifcPoint) {
                            I = { location: ifcPoint.point };
                        }
                    }
                }

                const volumeArray = [...this.viewer.scene.volumes, ...this.viewer.scene.planes];
                if (!I && volumeArray.length > 0) {
                    I = Utils.getMeshesIntersectionWithVolumes(e.drag.end, camera, this.viewer, volumeArray, {
                        newVolumeMesh: volume.uuid,
                        pickInvisible: false,
                    });
                }
            } else if (args.clip) {
                if (args.clipType == 'Potree') {
                    I = Utils.getMousePointCloudIntersection(e.drag.end, camera, this.viewer, this.viewer.scene.pointclouds, {
                        pickClipped: false,
                        pickInvisible: false,
                    });
                } else if (args.ifcHelper) {
                    let ifcPoint = await args.ifcHelper.castRayIfc(true);
                    if (ifcPoint) {
                        I = { location: ifcPoint.point };
                    }
                }
            }

            if (I) {
                volume.position.copy(I.location);

                let wp = volume.getWorldPosition(new THREE.Vector3()).applyMatrix4(camera.matrixWorldInverse);
                // let pp = new THREE.Vector4(wp.x, wp.y, wp.z).applyMatrix4(camera.projectionMatrix);
                let w = Math.abs(wp.z / 5);
                volume.scale.set(w, w, w);
            }
        };

        let drop = (e) => {
            volume.removeEventListener('drag', drag);
            volume.removeEventListener('drop', drop);

            cancel.callback(e);
        };

        cancel.callback = (e) => {
            volume.removeEventListener('drag', drag);
            volume.removeEventListener('drop', drop);
            this.viewer.removeEventListener('cancel_insertions', cancel.callback);
            if (customCallback) {
                customCallback(e.target);
            }
        };

        volume.addEventListener('drag', drag);
        volume.addEventListener('drop', drop);
        this.viewer.addEventListener('cancel_insertions', cancel.callback);

        this.viewer.inputHandler.startDragging(volume);

        return volume;
    }

    update() {
        if (!this.viewer.scene) {
            return;
        }

        let camera = this.viewer.scene.getActiveCamera();
        let renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
        let clientWidth = renderAreaSize.width;
        let clientHeight = renderAreaSize.height;

        let volumes = this.viewer.scene.volumes;
        for (let volume of volumes) {
            let label = volume.label;

            {
                let distance = label.position.distanceTo(camera.position);
                let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

                let scale = 70 / pr;
                label.scale.set(scale, scale, scale);
            }

            let calculatedVolume = volume.getVolume();
            calculatedVolume =
                (calculatedVolume / Math.pow(this.viewer.lengthUnit.unitspermeter, 3)) *
                Math.pow(this.viewer.lengthUnitDisplay.unitspermeter, 3); //convert to cubic meters then to the cubic display unit
            let text = Utils.addCommas(calculatedVolume.toFixed(3)) + ' ' + this.viewer.lengthUnitDisplay.code + '\u00B3';
            label.setText(text);
        }
    }

    render(params) {
        const renderer = this.viewer.renderer;

        const oldTarget = renderer.getRenderTarget();
        const savedClippingPlanes = renderer.clippingPlanes;
        renderer.clippingPlanes = [];

        if (params.renderTarget) {
            renderer.setRenderTarget(params.renderTarget);
        }
        renderer.render(this.scene, this.viewer.scene.getActiveCamera());
        renderer.setRenderTarget(oldTarget);
        renderer.clippingPlanes = savedClippingPlanes;
    }
}
